/**
 * Real-time market data availability — feeds, ingestion rates, gates, event log.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp,
  Database, Radio, RefreshCw, Wifi, WifiOff,
} from 'lucide-react';
import { titanApi, ApiError } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Skeleton } from '../ui/skeleton';

type FeedInfo = {
  configured?: boolean;
  connected?: boolean;
  streaming?: boolean;
  md_feed?: string | null;
  subscription?: Record<string, unknown>;
};

type DataAvailabilityPayload = {
  overall_status?: string;
  issues?: string[];
  controller?: {
    present?: boolean;
    is_running?: boolean;
    status?: string;
  };
  ingestion?: {
    quotes_per_minute?: number;
    last_quote_age_seconds?: number | null;
    last_quote_at?: string | null;
    consecutive_stale_gate?: number;
    data_feed_mode?: string;
    ws_quiet_warning?: string;
  };
  feeds?: {
    alpaca_rest?: FeedInfo;
    alpaca_ws?: FeedInfo;
    eodhd_ws?: FeedInfo;
    eodhd_rest?: FeedInfo;
    failover?: Record<string, unknown>;
  };
  gate?: {
    scan_safe?: boolean;
    watchdog?: Record<string, unknown>;
  };
  universe?: {
    subscribed_symbols?: number;
    pattern_universe_symbols?: number;
  };
  recent_events?: Array<{
    timestamp: string;
    source: string;
    event_type: string;
    level: string;
    message: string;
  }>;
};

const statusStyles: Record<string, string> = {
  healthy: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  degraded: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  offline: 'bg-rose-500/10 text-rose-600 border-rose-500/20',
  unknown: 'bg-muted text-muted-foreground',
};

const FeedBadge: React.FC<{ label: string; ok: boolean; detail?: string }> = ({
  label,
  ok,
  detail,
}) => (
  <div className="flex items-center justify-between gap-2 text-xs py-1">
    <span className="text-muted-foreground">{label}</span>
    <div className="flex items-center gap-1.5">
      {detail && <span className="text-[10px] text-muted-foreground">{detail}</span>}
      <Badge variant="outline" className={ok ? 'text-emerald-600' : 'text-rose-600'}>
        {ok ? 'OK' : 'Down'}
      </Badge>
    </div>
  </div>
);

const DataAvailabilityPanel: React.FC = () => {
  const [data, setData] = useState<DataAvailabilityPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [eventsOpen, setEventsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await titanApi.dataAvailability();
      setData((res.data || {}) as DataAvailabilityPayload);
      setError(null);
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Failed to load';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const status = (data?.overall_status || 'unknown').toLowerCase();
  const ingestion = data?.ingestion || {};
  const feeds = data?.feeds || {};
  const gate = data?.gate || {};
  const issues = data?.issues || [];

  const alpacaRestOk = Boolean(feeds.alpaca_rest?.connected);
  const alpacaWsOk = Boolean(feeds.alpaca_ws?.streaming);
  const eodhdWsOk = Boolean(feeds.eodhd_ws?.connected);

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between gap-2 p-3 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <Database className="h-4 w-4 text-primary shrink-0" />
          <span className="font-semibold text-sm">Data Availability</span>
          {!loading && (
            <Badge className={statusStyles[status] || statusStyles.unknown}>
              {status === 'healthy' && <CheckCircle2 className="h-3 w-3 mr-1" />}
              {status === 'degraded' && <AlertTriangle className="h-3 w-3 mr-1" />}
              {status === 'offline' && <WifiOff className="h-3 w-3 mr-1" />}
              {status}
            </Badge>
          )}
          {gate.scan_safe === false && (
            <Badge variant="outline" className="text-amber-700 text-[10px]">
              scans gated
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => load()} title="Refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="m-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {issues.length > 0 && status !== 'healthy' && (
        <Alert className="mx-3 mb-0 border-amber-500/30 bg-amber-500/5">
          <AlertDescription className="text-xs">
            {issues.join(' · ')}
          </AlertDescription>
        </Alert>
      )}

      {ingestion.ws_quiet_warning && (
        <Alert className="mx-3 mb-0">
          <AlertDescription className="text-xs">{ingestion.ws_quiet_warning}</AlertDescription>
        </Alert>
      )}

      {expanded && (
        <div className="p-3 space-y-3">
          {loading && !data ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-md border p-2">
                  <div className="text-[10px] text-muted-foreground">Quotes / min</div>
                  <div className="text-lg font-semibold tabular-nums">
                    {ingestion.quotes_per_minute ?? 0}
                  </div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-[10px] text-muted-foreground">Last quote</div>
                  <div className="text-lg font-semibold tabular-nums">
                    {ingestion.last_quote_age_seconds != null
                      ? `${Math.round(ingestion.last_quote_age_seconds)}s`
                      : '—'}
                  </div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-[10px] text-muted-foreground">Universe</div>
                  <div className="text-lg font-semibold tabular-nums">
                    {data?.universe?.pattern_universe_symbols ?? 0}
                  </div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-[10px] text-muted-foreground">Stale cycles</div>
                  <div className="text-lg font-semibold tabular-nums">
                    {ingestion.consecutive_stale_gate ?? 0}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-md border p-2 space-y-0.5">
                  <div className="text-xs font-medium flex items-center gap-1 mb-1">
                    <Wifi className="h-3 w-3" /> Feeds
                  </div>
                  <FeedBadge
                    label="Alpaca REST"
                    ok={alpacaRestOk}
                    detail={feeds.alpaca_rest?.md_feed || undefined}
                  />
                  <FeedBadge label="Alpaca WebSocket" ok={alpacaWsOk} />
                  <FeedBadge
                    label="EODHD WebSocket"
                    ok={eodhdWsOk}
                    detail={eodhdWsOk ? undefined : 'optional in RTH'}
                  />
                  <FeedBadge
                    label="EODHD REST"
                    ok={Boolean(feeds.eodhd_rest?.configured)}
                  />
                </div>
                <div className="rounded-md border p-2 space-y-1 text-xs">
                  <div className="font-medium flex items-center gap-1">
                    <Activity className="h-3 w-3" /> Scan gate
                  </div>
                  <p>
                    Mode:{' '}
                    <span className="font-mono">
                      {ingestion.data_feed_mode && ingestion.data_feed_mode !== 'unknown'
                        ? ingestion.data_feed_mode
                        : '—'}
                    </span>
                  </p>
                  <p>
                    Controller:{' '}
                    <span className={data?.controller?.is_running ? 'text-emerald-600' : 'text-amber-600'}>
                      {data?.controller?.is_running
                        ? data?.controller?.status || 'running'
                        : data?.controller?.present
                          ? 'stopped'
                          : 'not on this server'}
                    </span>
                  </p>
                  <p>
                    Scan safe:{' '}
                    <span className={gate.scan_safe ? 'text-emerald-600' : 'text-rose-600'}>
                      {gate.scan_safe === true ? 'yes' : gate.scan_safe === false ? 'no' : 'n/a'}
                    </span>
                  </p>
                  {feeds.alpaca_ws?.subscription && (
                    <p className="text-muted-foreground text-[10px]">
                      WS subs:{' '}
                      {String(
                        (feeds.alpaca_ws.subscription as Record<string, unknown>).output_count ??
                          '?',
                      )}
                      /
                      {String(
                        (feeds.alpaca_ws.subscription as Record<string, unknown>).input_count ??
                          '?',
                      )}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => setEventsOpen((v) => !v)}
                >
                  <Radio className="h-3 w-3" />
                  Recent pipeline events ({data?.recent_events?.length ?? 0})
                  {eventsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
                {eventsOpen && (
                  <div className="mt-2 max-h-40 overflow-y-auto rounded border divide-y text-[11px]">
                    {(data?.recent_events || []).length === 0 ? (
                      <p className="p-2 text-muted-foreground">No events yet — start the controller.</p>
                    ) : (
                      (data?.recent_events || []).map((ev, i) => (
                        <div key={`${ev.timestamp}-${i}`} className="px-2 py-1.5 flex gap-2">
                          <span className="text-muted-foreground shrink-0 font-mono">
                            {ev.timestamp?.slice(11, 19)}
                          </span>
                          <span className="text-primary shrink-0">{ev.source}</span>
                          <span className="truncate">{ev.message}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DataAvailabilityPanel;
